import { useRef } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { useContent } from '../lib/content';
import { useEdit } from '../lib/editContext';
import CornerOrnament from '../components/CornerOrnament';
import Editable from '../components/Editable';

function EditableBullet({ page, sectionNum, itemIndex, items, children }) {
  const { editMode, updateField } = useEdit();
  const ref = useRef(null);

  if (!editMode) {
    return <span>{children}</span>;
  }

  const handleBlur = () => {
    if (!ref.current) return;
    const newText = ref.current.innerText.trim();
    if (newText !== children) {
      const updated = [...items];
      updated[itemIndex] = newText;
      updateField(page, `section${sectionNum}_items`, updated.join('\n'));
    }
  };

  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className="editable-field"
      style={{ outline: 'none' }}
      onBlur={handleBlur}
      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ref.current?.blur(); } }}
      data-editable-page={page}
      data-editable-field={`section${sectionNum}_item${itemIndex}`}
    >
      {children}
    </span>
  );
}

export default function PoliciesPage() {
  const { get } = useContent('policies');

  const sections = [1, 2, 3, 4, 5].map(n => ({
    num: n,
    title: get(`section${n}_title`),
    items: (get(`section${n}_items`) || '').split('\n').filter(Boolean),
  })).filter(s => s.title);

  return (
    <div data-testid="policies-page" className="bg-[#101010]">
      <SEO
        title="Policies & FAQ — Booking, Check-In, House Rules"
        path="/policies"
        description="Read Plush Residence policies including booking & cancellation, check-in procedures, house rules, property care, and privacy."
        keywords="luxury rental policies, booking policy Ontario, cancellation policy short-term rental"
      />
      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gold text-[11px] uppercase tracking-[0.4em] font-bold mb-5">
            <Editable page="policies" field="label">{get('label')}</Editable>
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Editable page="policies" field="title" as="h1" className="font-heading text-5xl sm:text-6xl text-white font-bold mb-4">{get('title')}</Editable>
          </motion.div>
        </div>
      </section>

      {get('body') ? (
        <section className="pb-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-invert max-w-none bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-8" dangerouslySetInnerHTML={{ __html: get('body') }} />
          </div>
        </section>
      ) : (
        <section className="pb-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
            {sections.map((section, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="relative bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-8">
                <CornerOrnament size={14} offset="8px" />
                <Editable page="policies" field={`section${section.num}_title`} as="h2" className="font-heading text-xl text-white font-semibold mb-5">{section.title}</Editable>
                <ul className="space-y-3">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-white/45 text-sm leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold/40 mt-2 shrink-0" />
                      <EditableBullet page="policies" sectionNum={section.num} itemIndex={j} items={section.items}>{item}</EditableBullet>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
